import { useEffect } from 'react'

import { clsx } from 'clsx'
import { marked } from 'marked'

import { useTextDirection } from '@bettermode/common/hooks'
import { useTribeClient } from '@tribeplatform/react-sdk'
import { useAskAI, useAskAIAccess } from '@tribeplatform/react-sdk/hooks'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { T } from '../../../i18n/index.js'
import { ResultsView, SmartSearchEntityType } from '../../SmartSearch.types.js'
import { AiStarIcon } from '../AiStarIcon.js'
import { AskAISources } from '../AskAISources.js'
import { AskAISkeleton } from './AskAISkeleton.js'
import { NoQuestionEmptyState } from './NoQuestionEmptyState.js'

type Props = {
  question: string
  className?: string
  onSuggestionClick: (suggestion: string) => void
  headingClassName?: string
  view: ResultsView
  hasNewQuestionWaiting: boolean
  onSwitchToResults: (type: SmartSearchEntityType) => void
}

export const AskAI = ({
  question,
  className,
  onSuggestionClick,
  headingClassName,
  view,
  hasNewQuestionWaiting,
  onSwitchToResults,
}: Props) => {
  const {
    config: { enableAiSearch: isAiSearchEnabled },
  } = useTribeClient()
  const { canAskAi } = useAskAIAccess()
  const { askAI, response, isLoading, isError, error } = useAskAI()

  useEffect(() => {
    if (!question?.trim()) {
      return
    }

    askAI({ question })
  }, [askAI, question])

  const textDirection = useTextDirection(response)

  if (!canAskAi || !isAiSearchEnabled) {
    return null
  }

  if (!question) {
    return <NoQuestionEmptyState onItemClick={onSuggestionClick} view={view} />
  }

  const hasResponse = !!response

  return (
    <>
      <div className={className}>
        <div className="flex flex-col gap-4 px-4 lg:px-5 py-3">
          <div className="flex flex-col gap-2">
            <div
              className={clsx(
                'flex gap-2 items-center font-semibold',
                headingClassName,
              )}
            >
              <AiStarIcon isStatic={hasNewQuestionWaiting} />

              <span>
                <span>
                  <T
                    id="SmartSearch.AskAI.AIAssistant.Says"
                    defaultMessage="AI Assistant"
                  />
                </span>
              </span>
            </div>
          </div>

          {isLoading && !hasResponse && <AskAISkeleton />}

          <div
            dir={textDirection}
            className="ask-ai-response"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: marked(response) }}
          />

          {isError && (
            <div className="text-action-destructive flex gap-2 items-center">
              <T
                id="SmartSearch.AskAI.Error"
                defaultMessage="An error occurred! Please try again."
              />

              {error instanceof Error && error?.cause instanceof CloseEvent ? (
                <span>{error.cause.reason}</span>
              ) : (
                error.message && <span>{error.message}</span>
              )}

              <IconButton
                variant="secondary"
                size="sm"
                icon={<SvgIcon name="refresh" size="sm" />}
                onClick={() => {
                  askAI({ question })
                }}
              />
            </div>
          )}
        </div>
      </div>

      {hasResponse && (
        <AskAISources
          query={question}
          view={view}
          onSwitchToResults={onSwitchToResults}
        />
      )}
    </>
  )
}
